import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import '../Docs.css';

export default function IdleDetection() {
  return (
    <div className="docs-lite">
      <Helmet>
        <link
          rel="canonical"
          href="https://www.joegalaxy.net/docs/patterns/idle-detection"
        />
      </Helmet>

      <header className="docs-lite__header">
        <h1>Idle Detection: Distinguishing Connected from Active</h1>
      </header>

      <main className="docs-lite__content">
        <p>A connected session is not the same as an active human.</p>

        <p>
          Idle detection is a core presence pattern that separates <strong>transport connectivity</strong>{' '}
          from <strong>human engagement</strong>, preventing false activity signals and improving
          coordination accuracy.
        </p>

        <p>
          This document explains how to detect idle state correctly without breaking presence
          stability.
        </p>

        <hr />

        <section>
          <h2>Why idle detection matters</h2>
          <p>In real systems:</p>
          <ul>
            <li>browsers keep connections open while tabs are inactive</li>
            <li>users leave tabs open for hours or days</li>
            <li>mobile apps remain connected in the background</li>
            <li>network state does not reflect user attention</li>
          </ul>
          <p>If presence treats all connected sessions as active, downstream systems will:</p>
          <ul>
            <li>overestimate availability</li>
            <li>initiate interactions at the wrong time</li>
            <li>degrade trust in presence indicators</li>
            <li>create coordination failures</li>
          </ul>
          <p>Idle detection solves this by introducing <strong>activity-aware presence</strong>.</p>
        </section>

        <hr />

        <section>
          <h2>Connected does not mean active</h2>
          <p>A session may be connected while the user is:</p>
          <ul>
            <li>reading another tab</li>
            <li>away from their device</li>
            <li>backgrounded by the operating system</li>
            <li>idle without interaction</li>
          </ul>
          <p>Presence must model <strong>activity</strong>, not just connectivity.</p>
          <p>Idle detection provides the signal required to make this distinction.</p>
        </section>

        <hr />

        <section>
          <h2>Activity signals</h2>
          <p>Idle detection relies on <strong>lightweight activity signals</strong>, such as:</p>
          <ul>
            <li>keyboard input</li>
            <li>pointer movement</li>
            <li>touch events</li>
            <li>focus and visibility changes</li>
          </ul>
          <p>These signals should be:</p>
          <ul>
            <li>sampled, not streamed</li>
            <li>rate-limited</li>
            <li>privacy-conscious</li>
            <li>scoped to the session</li>
          </ul>
          <p>Idle detection does not require capturing user content or intent.</p>
        </section>

        <hr />

        <section>
          <h2>Session-level idle state</h2>
          <p>Idle state must be tracked at the <strong>session level</strong>, not the user level.</p>
          <p>Each session independently transitions through states such as:</p>
          <ul>
            <li>active</li>
            <li>idle</li>
            <li>away</li>
          </ul>
          <p>A user with multiple sessions may be active in one session and idle in another.</p>
          <p>User-level presence is derived from session state, not overwritten by it.</p>
        </section>

        <hr />

        <section>
          <h2>Time-based thresholds</h2>
          <p>Idle detection should be time-based, not event-based.</p>
          <p>Typical thresholds:</p>
          <ul>
            <li>short inactivity → idle</li>
            <li>longer inactivity → away</li>
            <li>extended inactivity → stale</li>
          </ul>
          <p>Exact timings vary by product and context.</p>
          <p>
            The key principle is that <strong>state is derived from elapsed time since last activity</strong>,
            not from a single event.
          </p>
        </section>

        <hr />

        <section>
          <h2>Avoiding false idle transitions</h2>
          <p>Common mistakes include:</p>
          <ul>
            <li>marking idle immediately on blur</li>
            <li>resetting idle on any background heartbeat</li>
            <li>using overly aggressive timeouts</li>
            <li>tying idle state to socket disconnects</li>
          </ul>
          <p>A session may briefly lose focus without the user leaving.</p>
          <p>Idle detection must be tolerant of transient state changes.</p>
        </section>

        <hr />

        <section>
          <h2>Deriving user presence with idle sessions</h2>
          <p>When aggregating user presence from sessions:</p>
          <ul>
            <li>active sessions take precedence</li>
            <li>idle sessions should not override active ones</li>
            <li>away sessions indicate reduced availability, not absence</li>
          </ul>
          <p>This ensures that idle detection improves accuracy without causing flapping.</p>
        </section>

        <hr />

        <section>
          <h2>Common failure modes</h2>
          <p>Idle detection fails when systems:</p>
          <ul>
            <li>treat idle as offline</li>
            <li>store idle state at the user level</li>
            <li>emit excessive idle events</li>
            <li>ignore multi-tab behavior</li>
            <li>conflate idle with disconnection</li>
          </ul>
          <p>Idle is a <strong>state of reduced engagement</strong>, not absence.</p>
        </section>

        <hr />

        <section>
          <h2>Summary</h2>
          <p>Idle detection is essential for truthful presence.</p>
          <p>A correct idle detection pattern:</p>
          <ul>
            <li>distinguishes activity from connectivity</li>
            <li>operates at the session level</li>
            <li>uses time-based thresholds</li>
            <li>tolerates transient focus changes</li>
            <li>feeds into derived user presence</li>
          </ul>
          <p>
            When implemented correctly, idle detection makes presence predictable, trustworthy, and
            coordination-ready.
          </p>
        </section>

        <hr />

        <section>
          <h2>Related docs</h2>
          <ul>
            <li>
              <Link to="/docs/index.md">Docs index</Link>
            </li>
            <li>
              <Link to="/docs/presence-online-offline.md">Online vs Offline Presence</Link>
            </li>
            <li>
              <Link to="/docs/whos-online-api.md">Who’s Online API</Link>
            </li>
            <li>
              <Link to="/docs/patterns/multi-tab.md">Multi-Tab Presence</Link>
            </li>
          </ul>
        </section>

        <p className="docs-lite__source">
          —
          <br />
          See this model running live:
          <br />
          <a href="https://www.joegalaxy.net/presence-demo">
            https://www.joegalaxy.net/presence-demo
          </a>
          <br />
          <a href="https://github.com/thejoegalaxy/presence-engine/blob/main/docs/patterns/idle-detection.md">
            View source on GitHub
          </a>
        </p>
      </main>
    </div>
  );
}
